import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { ChatCircleDots, X, PaperPlaneRight, Robot, Spinner } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function genSession() {
  return "float-" + Math.random().toString(36).slice(2, 10);
}

export default function FloatingChat() {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(genSession);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Üdv! Miben segíthetek?" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, open]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || loading) return;
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/chat`, { session_id: sessionId, message: msg });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: "Hiba — próbálja újra pár másodperc múlva." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        data-testid="floating-chat-toggle"
        onClick={() => setOpen(!open)}
        className="fixed bottom-24 right-6 z-[60] w-14 h-14 bg-[#0F0F0F] text-[#FFFFFF] rounded-sm shadow-xl flex items-center justify-center hover:bg-[#FFFFFF] hover:text-[#0F0F0F] transition-all hover:scale-105"
        aria-label="Chat"
      >
        {open ? <X size={24} weight="bold" /> : <ChatCircleDots size={26} weight="fill" />}
      </button>

      {open && (
        <div data-testid="floating-chat-panel" className="fixed bottom-44 right-6 z-[60] w-[calc(100%-3rem)] max-w-sm h-[480px] bg-[#FFFFFF] border border-[#E4E4E7] shadow-2xl flex flex-col">
          <div className="flex items-center justify-between p-3 border-b border-[#E4E4E7] bg-[#0F0F0F] text-[#FFFFFF]">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#FFFFFF] flex items-center justify-center rounded-sm">
                <Robot size={16} weight="fill" className="text-[#0F0F0F]" />
              </div>
              <div>
                <div className="font-display font-bold text-sm">Rapidgarázs AI</div>
                <div className="text-[10px] text-[#E4E4E7] flex items-center gap-1">
                  <div className="w-1.5 h-1.5 bg-[#DC2626] rounded-full pulse-dot" /> Online
                </div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Bezárás">
              <X size={18} />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-[#F5F5F5] chat-scroll">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 text-sm ${
                  m.role === "user" ? "bg-[#0F0F0F] text-[#FFFFFF]" : "bg-white border border-[#E4E4E7] text-[#0F0F0F]"
                }`}>
                  {m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-[#E4E4E7] px-3 py-2 text-sm flex items-center gap-2">
                  <Spinner size={14} className="animate-spin" /> ...
                </div>
              </div>
            )}
          </div>

          <div className="p-3 border-t border-[#E4E4E7] flex gap-2">
            <input
              data-testid="floating-chat-input"
              className="input-sharp flex-1 text-sm"
              placeholder="Kérdezz..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              disabled={loading}
            />
            <button
              data-testid="floating-chat-send"
              onClick={send}
              disabled={loading || !input.trim()}
              className="cta-primary px-3 disabled:opacity-50"
            >
              <PaperPlaneRight size={16} weight="fill" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
